import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export function DeliveryRefundPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    purchaseDate: "",
    reason: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = "El correo electrónico es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "El correo electrónico no es válido";
    }

    if (!formData.fullName) {
      newErrors.fullName = "El nombre completo es obligatorio";
    }

    if (!formData.purchaseDate) {
      newErrors.purchaseDate = "La fecha de compra es obligatoria";
    }

    if (!formData.reason) {
      newErrors.reason = "La descripción del motivo es obligatoria";
    } else if (formData.reason.length < 10) {
      newErrors.reason = "La descripción debe tener al menos 10 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Simulate API call
      setTimeout(() => {
        setShowSuccess(true);
      }, 1000);
    }
  };

  const handleBackToProfile = () => {
    navigate("/delivery/profile");
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-black text-white p-6">
        <div className="max-w-md mx-auto pt-12">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-6">¡Solicitud Enviada!</h1>
            <div className="bg-neutral-900 rounded-lg p-6 mb-8">
              <p className="text-sm leading-relaxed">
                <span className="text-green-400">
                  Solicitud recibida con éxito! 🥳
                </span>
                <br />
                <br />
                Nuestro equipo de análisis evaluará tu caso. Dependiendo de la
                institución financiera, el plazo de análisis y reembolso es de{" "}
                <span className="font-bold text-white">5 días ÚTILES</span>.
                <br />
                <br />
                <span className="text-orange-400 font-medium">
                  IMPORTANTE:
                </span>{" "}
                Cualquier nueva tentativa de solicitud sobre este caso ANTES del
                plazo reiniciará la contabilización.
              </p>
            </div>
            <Button
              onClick={handleBackToProfile}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3"
            >
              Volver al Perfil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-2 hover:bg-neutral-800 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">Solicitar Reembolso</h1>
        </div>

        {/* Info Message */}
        <div className="bg-neutral-900 rounded-lg p-4 mb-6">
          <p className="text-sm text-neutral-300 leading-relaxed">
            Completa correctamente la información para que la cancelación sea
            procesada junto a la institución financiera.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              1. Correo electrónico usado en la compra
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full bg-neutral-900 border rounded-lg px-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${
                errors.email ? "border-red-500" : "border-neutral-700"
              }`}
              placeholder="ejemplo@correo.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Full Name */}
          <div>
            <label
              htmlFor="fullName"
              className="block text-sm font-medium mb-2"
            >
              2. Nombre completo
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className={`w-full bg-neutral-900 border rounded-lg px-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${
                errors.fullName ? "border-red-500" : "border-neutral-700"
              }`}
              placeholder="Tu nombre completo"
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
            )}
          </div>

          {/* Purchase Date */}
          <div>
            <label
              htmlFor="purchaseDate"
              className="block text-sm font-medium mb-2"
            >
              3. Fecha de la Compra (Día/Mes/Año)
            </label>
            <input
              type="date"
              id="purchaseDate"
              name="purchaseDate"
              value={formData.purchaseDate}
              onChange={handleInputChange}
              className={`w-full bg-neutral-900 border rounded-lg px-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${
                errors.purchaseDate ? "border-red-500" : "border-neutral-700"
              }`}
            />
            {errors.purchaseDate && (
              <p className="text-red-500 text-sm mt-1">{errors.purchaseDate}</p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label htmlFor="reason" className="block text-sm font-medium mb-2">
              4. Describe el motivo exacto de tu solicitud
            </label>
            <textarea
              id="reason"
              name="reason"
              rows={4}
              value={formData.reason}
              onChange={handleInputChange}
              className={`w-full bg-neutral-900 border rounded-lg px-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors resize-none ${
                errors.reason ? "border-red-500" : "border-neutral-700"
              }`}
              placeholder="Explica detalladamente el motivo de tu solicitud de reembolso..."
            />
            {errors.reason && (
              <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
            )}
            <p className="text-xs text-neutral-500 mt-1">
              Mínimo 10 caracteres ({formData.reason.length}/10)
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 mt-8"
          >
            Enviar Solicitud de Reembolso
          </Button>
        </form>

        {/* Additional Info */}
        <div className="mt-6 p-4 bg-neutral-900/50 rounded-lg">
          <p className="text-xs text-neutral-400 text-center">
            Procesaremos tu solicitud en un plazo de 5 días útiles. Recibirás
            una confirmación por correo electrónico.
          </p>
        </div>
      </div>
    </div>
  );
}
